// Unpublished Work © 2020-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import GridLayout from 'Ui/components/grid/grid-layout';
import Tile from 'Ui/components/dashboard/tile';
import {createTileFromLayout, createTileMap} from 'Utils/dashboard-utils';
import {getTileInfo} from 'Ui/components/common/tile-constants/tile-info';

function createTileDataMap(tileData) {
    if (tileData.data) {
        const data = Array.isArray(tileData.data) ?
            tileData.data.find(({panels}) => panels) :
            tileData.data;

        if (data && data.panels) {
            return data.panels.reduce(createTileMap, {});
        }
    }

    return {};
}

function getTileData(data, tileData) {
    const isEmpty = Object.keys(tileData).length;

    if (Array.isArray(data)) {
        return isEmpty ? [tileData].concat(data) : data;
    }

    return isEmpty ? tileData : data;
}

function createTileComponents({
    appKeys,
    fetchDate,
    history,
    integrations,
    isgToggles,
    isMigrated,
    membershipId,
    myJdPermissions,
    thresholds,
    tileData,
    translations
}) {
    const tileMap = createTileDataMap(tileData);

    return tileData.tileIds.reduce((tileComponents, tileId) => {
        const newTileInfo = getTileInfo(tileId, isgToggles);

        if (!newTileInfo) {
            return tileComponents;
        }

        const tileMapData = tileMap[newTileInfo.name] || {};
        const newTileData = getTileData(tileData.data, tileMapData);

        const tileLayout = createTileFromLayout(newTileData, newTileInfo, integrations, {
            appKeys,
            isMigrated,
            myJdPermissions,
            isgToggles
        });

        if (tileLayout) {
            const onClickHandler = newTileInfo.onClick && (
                () => newTileInfo.onClick(
                    membershipId,
                    tileMapData.childCategory,
                    history,
                    newTileData.panelType,
                    newTileData.categoryType
                )
            );

            return tileComponents.concat(
                <Tile
                    columnCount={newTileInfo.columnCount}
                    component={newTileInfo.component}
                    fetchDate={fetchDate}
                    key={tileId}
                    onClick={onClickHandler}
                    thresholds={thresholds}
                    tileData={tileLayout}
                    translations={translations}
                />
            );
        }

        return tileComponents;
    }, []);
}

function TileLayout(props) {
    const {
        layout,
        tileData
    } = props;

    return (
        <>
            <GridLayout layout={layout}>
                {
                    tileData.reduce((tiles, tileDataItem) => {
                        const newTiles = createTileComponents({
                            ...props,
                            tileData: tileDataItem
                        });

                        return tiles.concat(newTiles);
                    }, [])
                }
            </GridLayout>
        </>
    );
}

TileLayout.propTypes = {
    appKeys: PropTypes.appKeys,
    fetchDate: PropTypes.string,
    history: PropTypes.history,
    integrations: PropTypes.arrayOf(PropTypes.object),
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    layout: PropTypes.layout,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    thresholds: PropTypes.thresholds,
    tileData: PropTypes.arrayOf(PropTypes.shape({
        data: PropTypes.tileData,
        tileIds: PropTypes.arrayOf(PropTypes.string)
    })),
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        appKeys: state.account.appKeys,
        isMigrated: state.membership.isMigrated,
        isgToggles: state.account.isgToggles,
        membershipId: state.membership.membershipId,
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps)(withRouter(TileLayout));
